(() => {
  if (window.productComparePageV5Initialized) return;
  window.productComparePageV5Initialized = true;

  const storageKey = 'compareItem';
  const idMapKey = 'compareProductIdMap';
  const specificationRows = [
    ['price', 'Price'],
    ['comparePrice', 'Regular Price'],
    ['productType', 'Type'],
    ['fatTires', 'Tire Size'],
    ['maxRange', 'Max Range'],
    ['topSpeed', 'Top Speed'],
    ['peakMotor', 'Peak Motor'],
    ['batteryCapacity', 'Battery Capacity'],
    ['maxLoad', 'Max Load'],
    ['availability', 'Availability'],
  ];

  const readJson = (key, fallback) => {
    try {
      return JSON.parse(localStorage.getItem(key)) || fallback;
    } catch (error) {
      return fallback;
    }
  };

  const escapeHtml = (value) => {
    const element = document.createElement('div');
    element.textContent = value || '-';
    return element.innerHTML;
  };

  const syncCompareState = (handles) => {
    const count = handles.length;
    document.querySelectorAll('[data-compare-count]').forEach((element) => {
      element.textContent = count;
    });
    document.querySelectorAll('.halo-compareProduct').forEach((element) => {
      element.classList.toggle('is-show', count > 1);
    });
    window.dispatchEvent(new CustomEvent('compare:updated', {
      detail: { count, handles: handles.slice() },
    }));
  };

  const updateUrl = (handles, idMap) => {
    const ids = handles.map((handle) => idMap[handle]).filter(Boolean);
    const url = new URL(window.location.href);
    if (ids.length) {
      url.searchParams.set('products', ids.join(','));
    } else {
      url.searchParams.delete('products');
    }
    url.searchParams.delete('handles');
    window.history.replaceState({}, '', url);
  };

  const getHeaderOffset = () => {
    const candidates = document.querySelectorAll(
      '.shopify-section-header-sticky, sticky-header, sticky-header-mobile'
    );
    let offset = 0;
    candidates.forEach((element) => {
      const rect = element.getBoundingClientRect();
      const style = window.getComputedStyle(element);
      if (rect.top <= 1 && rect.bottom > 0 && style.display !== 'none' && style.visibility !== 'hidden') {
        offset = Math.max(offset, rect.bottom);
      }
    });
    return Math.max(0, Math.round(offset));
  };

  const renderMatrix = (page, cards, state) => {
    const matrix = page.querySelector('[data-compare-matrix]');
    const sticky = page.querySelector('[data-compare-sticky]');
    const stickyRow = page.querySelector('[data-compare-sticky-row]');
    const productCount = cards.length;
    matrix.style.setProperty('--compare-product-count', productCount);
    stickyRow.style.setProperty('--compare-product-count', productCount);

    const headerRow = document.createElement('div');
    headerRow.className = 'product-compare-matrix__row product-compare-matrix__header';
    headerRow.setAttribute('data-compare-header', '');
    headerRow.innerHTML = '<div class="product-compare-matrix__label"></div>';
    cards.forEach((card) => headerRow.appendChild(card));

    const fragment = document.createDocumentFragment();
    fragment.appendChild(headerRow);
    specificationRows.forEach(([key, label]) => {
      const values = cards.map((card) => card.dataset[key] || '-');
      const row = document.createElement('div');
      row.className = 'product-compare-matrix__row product-compare-matrix__specification';
      row.dataset.compareSpecification = key;
      row.dataset.identical = String(values.every((value) => value === values[0]));
      row.innerHTML = '<div class="product-compare-matrix__label">' + escapeHtml(label) + '</div>' +
        values.map((value) => '<div class="product-compare-matrix__value">' + escapeHtml(value) + '</div>').join('');
      fragment.appendChild(row);
    });
    matrix.replaceChildren(fragment);

    stickyRow.innerHTML = headerRow.innerHTML;
    stickyRow.querySelectorAll('[id], [for], [data-compare-page-remove]').forEach((element) => {
      element.removeAttribute('id');
      element.removeAttribute('for');
      element.removeAttribute('data-compare-page-remove');
    });
    sticky.hidden = true;
    state.headerRow = headerRow;
    state.sticky = sticky;
    page.querySelector('[data-compare-content]').hidden = false;
  };

  const applyDifferenceFilter = (page) => {
    const differencesOnly = page.querySelector('[data-compare-differences]').checked;
    page.querySelectorAll('[data-compare-specification]').forEach((row) => {
      row.hidden = differencesOnly && row.dataset.identical === 'true';
    });
  };

  const setupStickyHeader = (page, state) => {
    const mainScroll = page.querySelector('[data-compare-scroll]');
    const stickyScroll = page.querySelector('[data-compare-sticky-scroll]');
    let syncing = false;

    const syncScroll = (source, target) => {
      if (syncing) return;
      syncing = true;
      target.scrollLeft = source.scrollLeft;
      window.requestAnimationFrame(() => { syncing = false; });
    };
    mainScroll.addEventListener('scroll', () => syncScroll(mainScroll, stickyScroll), { passive: true });
    stickyScroll.addEventListener('scroll', () => syncScroll(stickyScroll, mainScroll), { passive: true });

    const update = () => {
      if (!state.headerRow || !state.sticky) return;
      const headerOffset = getHeaderOffset();
      const headerRect = state.headerRow.getBoundingClientRect();
      const matrixRect = page.querySelector('[data-compare-matrix]').getBoundingClientRect();
      const stickyHeight = state.sticky.offsetHeight || 112;
      const shouldShow = headerRect.bottom <= headerOffset && matrixRect.bottom > headerOffset + stickyHeight;
      state.sticky.style.setProperty('--compare-sticky-top', headerOffset + 'px');
      state.sticky.hidden = !shouldShow;
      state.sticky.classList.toggle('is-visible', shouldShow);
    };

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    document.addEventListener('shopify:section:load', update);
    state.updateSticky = update;
  };

  const resolveHandles = async (selectedIds, idMap) => {
    let handles = selectedIds
      .map((id) => Object.keys(idMap).find((handle) => String(idMap[handle]) === id))
      .filter(Boolean);
    if (handles.length === selectedIds.length || !selectedIds.length) return handles;

    const response = await fetch('/products.json?limit=250');
    if (!response.ok) return handles;
    const catalog = await response.json();
    return selectedIds
      .map((id) => catalog.products.find((product) => String(product.id) === id)?.handle)
      .filter(Boolean);
  };

  const ensureCurrentMarkup = (page) => {
    document.body.classList.remove('product-compare-page-active');
    if (page.querySelector('[data-compare-content]')) return;

    const container = page.querySelector('.container');
    const status = page.querySelector('[data-compare-status]');
    const oldGrid = page.querySelector('[data-compare-grid]');
    const oldTable = page.querySelector('[data-compare-table]');
    const empty = page.querySelector('[data-compare-empty]');
    const content = document.createElement('div');
    content.className = 'product-compare-page__content';
    content.dataset.compareContent = '';
    content.hidden = true;
    content.innerHTML = '<div class="product-compare-page__tools">' +
      '<a class="product-compare-page__select" href="/collections/all"><span aria-hidden="true">+</span> Select Ebikes</a>' +
      '<label class="product-compare-page__differences"><input type="checkbox" data-compare-differences><span>Show Differences Only</span></label>' +
      '</div><div class="product-compare-matrix__scroll" data-compare-scroll><div class="product-compare-matrix" data-compare-matrix></div></div>';
    container.insertBefore(content, empty);
    oldGrid?.remove();
    oldTable?.remove();

    const sticky = document.createElement('div');
    sticky.className = 'product-compare-sticky';
    sticky.dataset.compareSticky = '';
    sticky.hidden = true;
    sticky.setAttribute('aria-hidden', 'true');
    sticky.innerHTML = '<div class="container"><div class="product-compare-sticky__scroll" data-compare-sticky-scroll><div class="product-compare-sticky__row" data-compare-sticky-row></div></div></div>';
    page.appendChild(sticky);
    if (status) status.textContent = 'Loading products...';
  };

  const initComparePage = async (page) => {
    ensureCurrentMarkup(page);
    const status = page.querySelector('[data-compare-status]');
    const empty = page.querySelector('[data-compare-empty]');
    const content = page.querySelector('[data-compare-content]');
    const params = new URLSearchParams(window.location.search);
    const storedHandles = readJson(storageKey, []);
    const selectedIds = (params.get('products') || '').split(',').filter(Boolean);
    const idMap = readJson(idMapKey, {});
    let handles = await resolveHandles(selectedIds, idMap);
    if (!handles.length) handles = storedHandles;

    handles.forEach((handle, index) => {
      if (selectedIds[index]) idMap[handle] = selectedIds[index];
    });
    localStorage.setItem(idMapKey, JSON.stringify(idMap));
    localStorage.setItem(storageKey, JSON.stringify(handles));
    syncCompareState(handles);

    if (handles.length < 2) {
      status.hidden = true;
      content.hidden = true;
      empty.hidden = false;
      return;
    }

    const responses = await Promise.all(handles.map(async (handle) => {
      const response = await fetch('/products/' + encodeURIComponent(handle) + '?view=ajax_product_compare_page');
      if (!response.ok) throw new Error('Unable to load ' + handle);
      return response.text();
    }));
    const parser = new DOMParser();
    const cards = responses.map((html) => parser.parseFromString(html, 'text/html').querySelector('[data-compare-card]'));
    if (cards.some((card) => !card)) throw new Error('Invalid comparison product response');

    const state = {};
    renderMatrix(page, cards, state);
    setupStickyHeader(page, state);
    applyDifferenceFilter(page);
    status.hidden = true;
    empty.hidden = true;
    state.updateSticky();

    page.querySelector('[data-compare-differences]').addEventListener('change', () => {
      applyDifferenceFilter(page);
      state.updateSticky();
    });

    page.addEventListener('change', (event) => {
      if (!event.target.matches('[data-compare-page-remove]') || event.target.checked) return;
      const card = event.target.closest('[data-compare-card]');
      const remaining = handles.filter((handle) => handle !== card.dataset.productHandle);
      handles = remaining;
      localStorage.setItem(storageKey, JSON.stringify(remaining));
      updateUrl(remaining, idMap);
      syncCompareState(remaining);

      if (remaining.length < 2) {
        content.hidden = true;
        empty.hidden = false;
        state.sticky.hidden = true;
        return;
      }
      const remainingCards = Array.from(page.querySelectorAll('[data-compare-matrix] [data-compare-card]'))
        .filter((item) => item.dataset.productHandle !== card.dataset.productHandle);
      renderMatrix(page, remainingCards, state);
      applyDifferenceFilter(page);
      state.updateSticky();
    });
  };

  const start = () => {
    const page = document.querySelector('[data-product-compare-page]');
    if (!page || page.dataset.compareInitialized === 'true') return;
    page.dataset.compareInitialized = 'true';
    initComparePage(page).catch(() => {
      const status = page.querySelector('[data-compare-status]');
      status.hidden = false;
      status.textContent = 'Unable to load the selected products. Please try again.';
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
